import { type ChangeEvent, useEffect, useState } from 'react'

import { onCheckDisabledConditions } from '@/app/utils'
import { type TEditMode } from '@/pages/Admins/helper'

interface IField {
    name: string
    value: string
    placeholder: string
    error: string | null
    isTouched: boolean
}

export const useFields = ({ loginVal, editMode }: { loginVal: string; editMode: TEditMode }) => {
    const initialLogin = { value: loginVal, error: null, isTouched: false, name: 'login', placeholder: 'Логин' }
    const initialNewPassword = {
        value: '',
        error: null,
        isTouched: false,
        name: 'newPassword',
        placeholder: 'Введите новый пароль'
    }
    const initialConfirmPassword = {
        value: '',
        error: null,
        isTouched: false,
        name: 'confirmPassword',
        placeholder: 'Подтвердите новый пароль'
    }

    const [login, setLogin] = useState<IField>(initialLogin)
    const [newPassword, setNewPassword] = useState<IField>(initialNewPassword)
    const [confirmPassword, setConfirmPassword] = useState<IField>(initialConfirmPassword)

    useEffect(() => {
        if (editMode === 'password') {
            if (!newPassword.value.length) {
                setNewPassword((prev) => ({ ...prev, error: 'Обязательное поле' }))
            } else if (
                !/^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$;^&*])[0-9a-zA-Z!@#$;^&*]{8,25}$/g.test(newPassword.value)
            ) {
                setNewPassword((prev) => ({ ...prev, error: 'Пароль не валиден' }))
            } else {
                setNewPassword((prev) => ({ ...prev, error: null }))
            }

            if (!confirmPassword.value.length) {
                setConfirmPassword((prev) => ({ ...prev, error: 'Обязательное поле' }))
            } else if (newPassword.value !== confirmPassword.value) {
                setConfirmPassword((prev) => ({ ...prev, error: 'Не соответствует паролю' }))
            } else {
                setConfirmPassword((prev) => ({ ...prev, error: null }))
            }
        }
    }, [newPassword.value, confirmPassword.value, editMode])

    const onInitializeFields = () => {
        setLogin(initialLogin)
        setNewPassword(initialNewPassword)
        setConfirmPassword(initialConfirmPassword)
    }

    const onChangeLogin = (e: ChangeEvent<HTMLInputElement>) => setLogin((prev) => ({ ...prev, value: e.target.value }))

    const onChangeNewPassword = (e: ChangeEvent<HTMLInputElement>) =>
        setNewPassword((prev) => ({ ...prev, value: e.target.value }))

    const onChangeConfirmPassword = (e: ChangeEvent<HTMLInputElement>) =>
        setConfirmPassword((prev) => ({ ...prev, value: e.target.value }))

    const onTouchLogin = () => setLogin((prev) => ({ ...prev, isTouched: true }))
    const onTouchNewPassword = () => setNewPassword((prev) => ({ ...prev, isTouched: true }))
    const onTouchConfirmPassword = () => setConfirmPassword((prev) => ({ ...prev, isTouched: true }))

    const onZeroLogin = () => setLogin((prev) => ({ ...prev, value: '' }))
    const onZeroNewPassword = () => setNewPassword((prev) => ({ ...prev, value: '' }))
    const onZeroConfirmPassword = () => setConfirmPassword((prev) => ({ ...prev, value: '' }))

    const loginError = login.isTouched && login.error ? login.error : null
    const newPasswordError = newPassword.isTouched && newPassword.error ? newPassword.error : null
    const confirmPasswordError = confirmPassword.isTouched && confirmPassword.error ? confirmPassword.error : null

    const isValidFields = onCheckDisabledConditions(
        Boolean(!newPassword.error),
        Boolean(newPassword.isTouched),
        Boolean(!confirmPassword.error),
        Boolean(confirmPassword.isTouched)
    )

    return {
        login,
        loginError,
        onChangeLogin,
        onTouchLogin,
        onZeroLogin,
        newPassword,
        newPasswordError,
        onChangeNewPassword,
        onTouchNewPassword,
        onZeroNewPassword,
        confirmPassword,
        confirmPasswordError,
        onChangeConfirmPassword,
        onTouchConfirmPassword,
        onZeroConfirmPassword,
        isValidFields,
        onInitializeFields
    }
}
